/**
 * Kartica »Program po letih« na strani Uvoz (docs/ARHITEKTURA.md, 5.2).
 *
 * Iz katerega racunovodskega programa so izvozi posameznega leta: vir
 * podjetja od zacetka in prehodi, ki jih doda skrbnik (do 2025 Minimax, od
 * 2026 Pantheon). Obrazec za prehod se odpre sele ob kliku in pove, kaj se bo
 * zgodilo, preden se karkoli shrani.
 */
import { VIRI, nazivVira, virZaLetoIzObdobij, type ObdobjeVira, type ProgramPoLetih as Program } from '@koda/shared';
import { useState, type FormEvent, type JSX } from 'react';

import { api } from '../lib/api';
import { datum } from '../lib/format';

interface Lastnosti {
  podjetje: string;
  program: Program;
  /** Leta z uvozenimi knjizbami; prehod od leta z uvozom iz drugega programa ni mogoc. */
  letaSPodatki: readonly number[];
  naSpremembo: (novo: Program) => void;
}

/** Znacka programa; ista v seznamu obdobij in pri letih s podatki. */
export function ZnackaVira({ vir }: { vir: string }): JSX.Element {
  return <span className={`znacka-vira vir-${vir}`}>{nazivVira(vir) ?? vir}</span>;
}

function opisLet(o: ObdobjeVira): string {
  if (o.odLeta === null) return o.doLeta === null ? 'vsa leta' : `do ${String(o.doLeta)}`;
  if (o.doLeta === null) return `od ${String(o.odLeta)}`;
  return o.doLeta === o.odLeta ? `samo ${String(o.odLeta)}` : `${String(o.odLeta)}–${String(o.doLeta)}`;
}

export function ProgramPoLetih({ podjetje, program, letaSPodatki, naSpremembo }: Lastnosti): JSX.Element {
  const [odprto, setOdprto] = useState(false);
  const [odLeta, setOdLeta] = useState(String(new Date().getFullYear()));
  const [doLeta, setDoLeta] = useState('');
  const [vir, setVir] = useState(VIRI.find((v) => v.uvozPodprt)?.koda ?? 'pantheon');
  const [napaka, setNapaka] = useState<string | null>(null);
  const [tece, setTece] = useState(false);

  const leto = Number.parseInt(odLeta, 10);
  const letoVeljavno = Number.isInteger(leto) && leto >= 1990 && leto <= 2999;
  // Neobvezno leto »do«: prehod velja samo do njega, potem spet prejsnji program.
  const letoDo = doLeta.trim() === '' ? null : Number.parseInt(doLeta, 10);
  const doVeljavno = letoDo === null || (Number.isInteger(letoDo) && letoDo >= leto && letoDo <= 2999);
  const virPrej = letoVeljavno ? virZaLetoIzObdobij(program.obdobja, leto) : undefined;
  // Program po koncu obdobja: tisti, ki danes velja za leto po »do« (ni nujno
  // isti kot pred prehodom - pred obstojecim prehodom je lahko drug).
  const virPoKoncu = letoDo !== null && doVeljavno ? virZaLetoIzObdobij(program.obdobja, letoDo + 1) : undefined;
  const istiProgram = virPrej === vir;
  // Leta prehoda, ki ze imajo podatke: tam je prehod mogoc sele, ko so uvozi
  // iz prejsnjega programa nadomesceni (streznik to zavrne).
  const zasedena = letoVeljavno ? letaSPodatki.filter((l) => l >= leto && (letoDo === null || l <= letoDo)) : [];
  // Leta po prehodu s podatki: namig, da se prehod lahko omeji z letom »do«.
  const kasnejsaSPodatki = letoVeljavno && letoDo === null ? letaSPodatki.filter((l) => l > leto) : [];

  const posljiPrehod = (d: FormEvent): void => {
    d.preventDefault();
    if (!letoVeljavno || !doVeljavno || istiProgram) return;
    setTece(true);
    setNapaka(null);
    api
      .dodajPrehodPrograma(podjetje, leto, vir, letoDo)
      .then((novo) => {
        naSpremembo(novo);
        setOdprto(false);
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Prehoda ni bilo mogoče shraniti.');
      })
      .finally(() => {
        setTece(false);
      });
  };

  const odstrani = (leto: number): void => {
    setNapaka(null);
    api
      .odstraniPrehodPrograma(podjetje, leto)
      .then(naSpremembo)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Prehoda ni bilo mogoče odstraniti.');
      });
  };

  return (
    <section className="kartica" aria-labelledby="program-po-letih">
      <div className="glava-kartice">
        <h2 id="program-po-letih">Program po letih</h2>
        {!odprto && (
          <button
            type="button"
            className="gumb-dodaj"
            onClick={() => {
              setOdprto(true);
              setNapaka(null);
            }}
          >
            <span className="plus" aria-hidden="true" />
            Dodaj prehod
          </button>
        )}
      </div>
      <p className="podnaslov">
        Iz katerega računovodskega programa so izvozi posameznega leta. Po tem se portal odloči,
        kako bere stolpce, vrsto temeljnice in otvoritev. Ko stranka zamenja program, dodaj prehod;
        prejšnja leta ostanejo, kot so.
      </p>
      <ul className="obdobja-virov">
        {program.obdobja.map((o) => (
          <li key={o.odLeta ?? 'zacetek'}>
            <span className={`pika-vira vir-${o.vir}`} aria-hidden="true" />
            <span className="leta-obdobja">{opisLet(o)}</span>
            <ZnackaVira vir={o.vir} />
            <span className="drobno">
              {o.dodano === null ? (
                'vir podjetja od ustanovitve'
              ) : (
                <>
                  dodal skrbnik, {datum(o.dodano)}
                  {o.odstranljivo && o.odLeta !== null && (
                    <>
                      {' · '}
                      <button
                        type="button"
                        className="gumb-besedilo"
                        onClick={() => {
                          odstrani(o.odLeta!);
                        }}
                      >
                        odstrani
                      </button>
                    </>
                  )}
                </>
              )}
            </span>
          </li>
        ))}
      </ul>

      {odprto && (
        <form className="obrazec obrazec-prehoda" onSubmit={posljiPrehod}>
          <label>
            Od poslovnega leta
            <input
              type="number"
              min={1990}
              max={2999}
              value={odLeta}
              onChange={(d) => {
                setOdLeta(d.target.value);
              }}
              required
            />
          </label>
          <label>
            Do poslovnega leta (neobvezno)
            <input
              type="number"
              min={letoVeljavno ? leto : 1990}
              max={2999}
              value={doLeta}
              placeholder="do naslednjega prehoda"
              onChange={(d) => {
                setDoLeta(d.target.value);
              }}
            />
          </label>
          <label>
            Program
            <select
              value={vir}
              onChange={(d) => {
                setVir(d.target.value);
              }}
            >
              {VIRI.filter((v) => v.uvozPodprt).map((v) => (
                <option key={v.koda} value={v.koda}>
                  {v.naziv}
                </option>
              ))}
            </select>
          </label>
          {letoVeljavno && (
            <div>
              <div className="drobno">Kaj se zgodi</div>
              {istiProgram ? (
                <p className="namig">
                  Za leto {odLeta} že velja {nazivVira(vir) ?? vir}; prehod ni potreben.
                </p>
              ) : (
                <ul className="posledice-prehoda">
                  <li>
                    Leta <strong>do {String(leto - 1)}</strong> ostanejo {nazivVira(virPrej) ?? virPrej}: knjižbe,
                    izvirne vrstice in otvoritve se ne spremenijo.
                  </li>
                  <li>
                    {letoDo === null ? (
                      <>
                        Od <strong>{odLeta}</strong> naprej
                      </>
                    ) : letoDo === leto ? (
                      <>
                        Samo za leto <strong>{odLeta}</strong>
                      </>
                    ) : (
                      <>
                        Za leta <strong>{odLeta}–{String(letoDo)}</strong>
                      </>
                    )}{' '}
                    portal sprejme samo izvoz iz programa {nazivVira(vir) ?? vir}; izvoz iz drugega programa za ta
                    leta zavrne s pojasnilom.
                  </li>
                  {letoDo !== null && doVeljavno && (
                    <li>
                      Od <strong>{String(letoDo + 1)}</strong> naprej {virPoKoncu === virPrej ? 'spet ' : ''}velja{' '}
                      {nazivVira(virPoKoncu) ?? virPoKoncu}; leta od tam naprej se ne spremenijo.
                    </li>
                  )}
                  <li>
                    Pri vrstah temeljnic bo za otvoritev od leta {odLeta} treba nastaviti vrsto, kot jo
                    izvaža {nazivVira(vir) ?? vir}, poleg dosedanje.
                  </li>
                  <li>Sprememba se zapiše v dnevnik dejanj.</li>
                </ul>
              )}
              {!doVeljavno && (
                <p className="opozorilo">Leto »do« mora biti enako letu »od« ali kasnejše.</p>
              )}
              {zasedena.length > 0 && !istiProgram && (
                <p className="opozorilo">
                  Leto {zasedena.map(String).join(', ')} že ima uvoz. Če je iz prejšnjega programa,
                  je prehod mogoč šele, ko je ta uvoz nadomeščen ali izbrisan, da se v enem letu ne
                  mešata dva programa.
                  {kasnejsaSPodatki.length > 0 && (
                    <>
                      {' '}
                      Če naj prehod velja samo za nekaj let (npr. samo {odLeta}), vpiši še leto »do«; leta po
                      njem ostanejo pri dosedanjem programu.
                    </>
                  )}
                </p>
              )}
            </div>
          )}
          {napaka !== null && (
            <p className="napaka" role="alert">
              {napaka}
            </p>
          )}
          <div className="gumbi-nastavitev">
            <button type="submit" className="gumb" disabled={tece || !letoVeljavno || !doVeljavno || istiProgram}>
              Shrani prehod
            </button>
            <button
              type="button"
              onClick={() => {
                setOdprto(false);
                setNapaka(null);
              }}
            >
              Prekliči
            </button>
          </div>
        </form>
      )}
      {!odprto && napaka !== null && (
        <p className="napaka" role="alert">
          {napaka}
        </p>
      )}
    </section>
  );
}
